import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS, NAV_EXTERNAL } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import LoginButton from "@/components/LoginButton";
import { List, X, Plus } from "@phosphor-icons/react";
import LanguageSelector from "@/components/LanguageSelector";
import LocationPill from "@/components/LocationPill";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <header data-testid="site-nav" className="fixed top-0 left-0 right-0 z-50">
      {/* Thin top strip: geographic context. Kept out of the main nav row so
          it can never push primary tab links off-screen. */}
      <div className="bg-[#050B12]/90 backdrop-blur-xl border-b border-[#F5EBDC]/5">
        <div className="max-w-[1600px] mx-auto flex items-center justify-end gap-3 px-6 md:px-10 py-1.5">
          <LocationPill compact />
          <div className="hidden md:block"><LanguageSelector variant="dark" /></div>
        </div>
      </div>
      <div className="bg-[#0A1620]/85 backdrop-blur-xl border-b border-[#F5EBDC]/10">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4 px-6 md:px-10 py-3">
          <Link to="/" className="flex flex-col items-start leading-tight group shrink-0" data-testid="brand-link">
            <span className="font-serif-display text-xl md:text-[22px] tracking-tight text-[#F5EBDC]">
              NaturePulse<span className="text-[10px] align-super text-[#E4A15C] ml-0.5">™</span>
            </span>
            <span className="hidden md:inline font-mono-tech text-[9px] tracking-[0.28em] text-[#E5D8C4]/55 uppercase -mt-0.5">
              A Social Pulse Endeavor
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 min-w-0 flex-wrap justify-end">
            {NAV_LINKS.map((l) => {
              const isFeatured = l.label === "Weather Almanac";
              return (
                <NavLink
                  key={l.label}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                  className={({ isActive }) =>
                    isFeatured
                      ? `px-2.5 py-1.5 ml-1 mr-1 rounded-full font-mono-tech text-[10px] tracking-[0.22em] uppercase whitespace-nowrap transition-colors ${
                          isActive
                            ? "bg-[#E4A15C] text-[#0A1620]"
                            : "bg-[#E4A15C]/15 text-[#E4A15C] hover:bg-[#E4A15C]/25 border border-[#E4A15C]/40"
                        }`
                      : `px-1.5 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase transition-colors whitespace-nowrap ${
                          isActive ? "text-[#E4A15C]" : "text-[#E5D8C4]/75 hover:text-[#F5EBDC]"
                        }`
                  }
                >
                  {l.label}
                </NavLink>
              );
            })}
            {NAV_EXTERNAL.map((l) => (
              <a
                key={l.label}
                href={l.to}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g,'-')}`}
                className="hidden xl:inline-block px-2 py-2 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#E5D8C4]/55 hover:text-[#F5EBDC] transition-colors whitespace-nowrap"
              >
                {l.label} ↗
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/#identify"
              data-testid="nav-add-sighting"
              className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[#F5EBDC]/30 text-[#F5EBDC] px-3.5 py-1.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-[#F5EBDC] hover:text-[#0A1620] transition-colors"
            >
              <Plus size={12} weight="bold" /> Add Sighting
            </Link>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  data-testid="nav-dashboard"
                  className="hidden md:inline font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#E5D8C4]/75 hover:text-[#F5EBDC]"
                >Dashboard</Link>
                <button
                  onClick={logout}
                  data-testid="logout-btn"
                  className="hidden md:inline-flex items-center gap-2 rounded-full border border-[#F5EBDC]/30 text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-[#F5EBDC] hover:text-[#0A1620] transition-colors"
                >Sign out</button>
                <img src={user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'U')}&background=E4A15C&color=0A1620`} alt={user.name} className="w-8 h-8 rounded-full border border-[#F5EBDC]/25" />
              </>
            ) : (
              <LoginButton label="Log in" className="border-[#F5EBDC]/30 text-[#F5EBDC] hover:bg-[#F5EBDC] hover:text-[#0A1620]" />
            )}
            <button
              className="lg:hidden text-[#F5EBDC] p-2"
              onClick={() => setOpen(v => !v)}
              data-testid="nav-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} weight="thin" /> : <List size={22} weight="thin" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-[#0A1620]/95 backdrop-blur-xl border-b border-[#F5EBDC]/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={`py-2 font-serif-display text-2xl ${location.pathname===l.to ? "text-[#E4A15C]" : "text-[#F5EBDC]"}`}
                >{l.label}</Link>
              ))}
              {NAV_EXTERNAL.map((l) => (
                <a key={l.label} href={l.to} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="py-2 font-serif-display text-xl text-[#E5D8C4]/80">
                  {l.label} <span className="text-xs align-middle">↗</span>
                </a>
              ))}
              {user && (
                <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 font-serif-display text-2xl text-[#F5EBDC]">Dashboard</Link>
              )}
              <Link to="/civic-reports#add" onClick={() => setOpen(false)} className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#F5EBDC]/30 text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase w-fit">
                <Plus size={12} weight="bold" /> Add Sighting
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
